"use client";

import { useEffect, useState } from "react";

// The content script on lokerdollar.com sets this attribute on <html> when the
// extension is installed (extension/src/content/index.ts). Absence after a short
// settle window = not installed. Kept in sync with the extension marker name.
export const EXTENSION_MARKER_ATTR = "data-lokerdollar-extension";

/** How long to wait for the marker before answering "not installed". */
export const EXTENSION_DETECT_WINDOW_MS = 4000;

/**
 * Resolves whether the lokerdollar extension is installed by watching for the
 * DOM marker the content script sets. Returns `null` while still checking,
 * `true`/`false` once resolved.
 *
 * The content script runs at document_idle, which on a slow phone or a busy
 * job page can land well after 1.6s (the old poll window), so installed users
 * were shown the install prompt. The window is now
 * {@link EXTENSION_DETECT_WINDOW_MS}, and a MutationObserver keeps watching
 * after the window closes: a late marker still flips the answer to `true` and
 * hides any install prompt already on screen.
 *
 * Shared by `ExtensionInstallNudge`, `ExtensionBanner` and `JobApplyCta`'s
 * profile-incomplete banner (see `src/lib/profile/completeness.ts`).
 */
export function useExtensionInstalled(): boolean | null {
	const [installed, setInstalled] = useState<boolean | null>(null);

	useEffect(() => {
		const root = document.documentElement;
		if (root.hasAttribute(EXTENSION_MARKER_ATTR)) {
			setInstalled(true);
			return;
		}
		const observer = new MutationObserver(() => {
			if (root.hasAttribute(EXTENSION_MARKER_ATTR)) {
				setInstalled(true);
				observer.disconnect();
				window.clearTimeout(timer);
			}
		});
		observer.observe(root, {
			attributes: true,
			attributeFilter: [EXTENSION_MARKER_ATTR],
		});
		const timer = window.setTimeout(() => {
			if (!root.hasAttribute(EXTENSION_MARKER_ATTR)) setInstalled(false);
		}, EXTENSION_DETECT_WINDOW_MS);
		return () => {
			observer.disconnect();
			window.clearTimeout(timer);
		};
	}, []);

	return installed;
}
